import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { MediaAsset } from '@nativewaves/nw-cloud-sdk/media'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useMediaAssetCreateMutation, useMediaAssetDeleteMutation } from 'hooks/mutations/media/MediaAsset'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<MediaAsset>) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createAsset, ...assetCreateMutation } = useMediaAssetCreateMutation()
  const { mutate: deleteAsset } = useMediaAssetDeleteMutation()

  const handleDuplication = useCallback(() => {
    createAsset(
      { mediaAssetInsert: { ...row.original, name: `${row.original.name} (Copy)` } },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createAsset, onClose, row.original])

  const handleDeletion = useCallback(() => {
    onClose()

    deleteAsset(
      { assetId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:MediaPlatform.nameDelete', { name: row.original.name }), {
            variant: 'success',
          }),
      },
    )
  }, [deleteAsset, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  return (
    <>
      <MenuItem onClick={handleDuplication} disabled={assetCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {assetCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeletion} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
